import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';
import { useAuth } from './AuthContext';
import { driverService } from '../services/driverService';
import { initAudio, startAlert, stopAlert } from '../services/alertSoundService';

const isWeb = Platform.OS === 'web';

// Driver mode constants
export const DRIVER_MODE = {
  IDLE: 'IDLE',
  INCOMING: 'INCOMING',
  ACTIVE: 'ACTIVE',
};

const RideContext = createContext({});

export const useRide = () => {
  const context = useContext(RideContext);
  if (!context) {
    throw new Error('useRide must be used within a RideProvider');
  }
  return context;
};

export const RideProvider = ({ children }) => {
  const { user } = useAuth();

  // Driver state
  const [isOnline, setIsOnline] = useState(false);
  const [location, setLocation] = useState(null);
  const [lastGpsUpdate, setLastGpsUpdate] = useState('Never');
  const [debugLog, setDebugLog] = useState('Initializing...');

  // Ride state
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeRide, setActiveRide] = useState(null);

  // Derived driver mode — single source of truth for UI state
  const driverMode = activeRide
    ? DRIVER_MODE.ACTIVE
    : (pendingRequests.length > 0 && isOnline)
      ? DRIVER_MODE.INCOMING
      : DRIVER_MODE.IDLE;

  // GPS refs
  const locationSub = useRef(null);
  const locationInterval = useRef(null);
  const latestCoords = useRef(null);

  // Polling refs
  const pollInterval = useRef(null);
  const knownRideIds = useRef(new Set());
  const isFirstFetch = useRef(true);

  // Prevent double-init
  const initialized = useRef(false);

  // ─── Load initial state on login ───
  useEffect(() => {
    if (user && !initialized.current) {
      initialized.current = true;
      loadInitialState();
    }
    if (!user) {
      initialized.current = false;
      cleanup();
    }
    return () => {};
  }, [user]);

  // ─── Polling: when online and no active ride, poll for requests ───
  useEffect(() => {
    if (isOnline && !activeRide && user) {
      console.log('[RideContext] Starting ride request polling (online, no active ride)');
      startPolling();
    } else {
      stopPolling();
      if (!isOnline) {
        setPendingRequests([]);
        stopAlert();
      }
    }
    return () => stopPolling();
  }, [isOnline, activeRide, user]);

  // ─── GPS: when online OR active ride, track location ───
  useEffect(() => {
    if ((isOnline || activeRide) && user) {
      startGPS();
    } else if (!isOnline && !activeRide) {
      stopGPS();
    }
    return () => {};
  }, [isOnline, activeRide, user]);

  // ─── Sound: play when pending requests exist ───
  useEffect(() => {
    if (pendingRequests.length > 0 && isOnline) {
      console.log('[RideContext] Pending requests:', pendingRequests.length, '- starting alert sound');
      startAlert();
    } else {
      stopAlert();
    }
  }, [pendingRequests.length, isOnline]);

  const loadInitialState = async () => {
    try {
      console.log('[RideContext] loadInitialState: loading...');

      // On web, NEVER auto-restore online status.
      // The driver must press GO ONLINE each session so that initAudio()
      // runs in a user gesture context (required by Chrome/Safari).
      // On native, we can restore since audio doesn't need a gesture.
      if (!isWeb) {
        const savedStatus = await AsyncStorage.getItem('driverOnlineStatus');
        if (savedStatus !== null) {
          const online = JSON.parse(savedStatus);
          setIsOnline(online);
          console.log('[RideContext] Restored online status:', online);
        }
      } else {
        // Clear any saved online status so it doesn't confuse things
        await AsyncStorage.setItem('driverOnlineStatus', JSON.stringify(false));
        console.log('[RideContext] Web: starting offline (user must press GO ONLINE)');
      }

      // Check for active ride from backend
      try {
        const response = await driverService.getCurrentRide();
        if (response.success && response.data.ride) {
          console.log('[RideContext] Found active ride from backend:', response.data.ride.id, response.data.ride.status);
          setActiveRide(response.data.ride);
          console.log('[RideContext] setActiveRide → restored ride', response.data.ride.id);
          // If there's an active ride, go online automatically
          // (on web this is OK — the ride is already accepted, no sound needed)
          setIsOnline(true);
        } else {
          console.log('[RideContext] No active ride found');
        }
      } catch (e) {
        console.log('[RideContext] getCurrentRide failed (backend may be unavailable):', e.message);
      }
    } catch (error) {
      console.error('[RideContext] loadInitialState error:', error);
    }
  };

  // ─── Polling for ride requests ───
  const startPolling = () => {
    stopPolling();
    fetchRideRequests();
    pollInterval.current = setInterval(fetchRideRequests, 10000);
  };

  const stopPolling = () => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
  };

  const fetchRideRequests = async () => {
    try {
      const response = await driverService.getAvailableRides();
      if (response.success) {
        const rides = response.data.rides.map(ride => ({
          ...ride,
          createdAt: new Date(ride.requestedAt || ride.createdAt),
        }));

        console.log('[RideContext] Polled rides:', rides.length, rides.length > 0 ? `(first: ${rides[0].id.slice(0, 8)}...)` : '');

        knownRideIds.current = new Set(rides.map(r => r.id));
        isFirstFetch.current = false;

        setPendingRequests(rides);
      }
    } catch (error) {
      console.error('[RideContext] fetchRideRequests error:', error.message);
      setDebugLog('Poll failed: ' + error.message);
    }
  };

  // ─── GPS tracking ───
  const startGPS = async () => {
    // Don't restart if already running
    if (locationSub.current) return;

    setDebugLog('Starting GPS tracking...');

    if (isWeb) {
      if (!navigator.geolocation) {
        setDebugLog('Browser geolocation not supported');
        return;
      }

      const watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          const coords = { latitude, longitude };
          latestCoords.current = coords;
          setLocation(coords);
          setLastGpsUpdate(new Date().toLocaleTimeString());
          setDebugLog(`GPS: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        },
        (err) => {
          setDebugLog('GPS watch error: ' + err.message);
          console.error('[RideContext] watchPosition error:', err.message);
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
      locationSub.current = { remove: () => navigator.geolocation.clearWatch(watchId) };

      // POST to backend every 8 seconds
      locationInterval.current = setInterval(async () => {
        const coords = latestCoords.current;
        if (!coords) return;
        try {
          await driverService.updateLocation(coords.latitude, coords.longitude);
          setDebugLog(`Sent: ${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`);
        } catch (e) {
          setDebugLog('POST failed: ' + e.message);
        }
      }, 8000);

      // Initial position
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          latestCoords.current = { latitude, longitude };
          setLocation({ latitude, longitude });
          setLastGpsUpdate(new Date().toLocaleTimeString());
          try {
            await driverService.updateLocation(latitude, longitude);
            setDebugLog(`Initial: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          } catch (e) {
            setDebugLog('Initial POST failed: ' + e.message);
          }
        },
        (err) => setDebugLog('Initial GPS failed: ' + err.message),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          setDebugLog('GPS permission denied');
          return;
        }

        locationSub.current = await Location.watchPositionAsync(
          { accuracy: Location.Accuracy.High, distanceInterval: 10, timeInterval: 5000 },
          (loc) => {
            const coords = { latitude: loc.coords.latitude, longitude: loc.coords.longitude };
            latestCoords.current = coords;
            setLocation(coords);
            setLastGpsUpdate(new Date().toLocaleTimeString());
          }
        );

        locationInterval.current = setInterval(async () => {
          const coords = latestCoords.current;
          if (!coords) return;
          try {
            await driverService.updateLocation(coords.latitude, coords.longitude);
            setDebugLog(`Sent: ${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`);
          } catch (e) {
            setDebugLog('POST failed: ' + e.message);
          }
        }, 8000);

        const initialLoc = await Location.getCurrentPositionAsync({});
        if (initialLoc) {
          const { latitude, longitude } = initialLoc.coords;
          latestCoords.current = { latitude, longitude };
          setLocation({ latitude, longitude });
          await driverService.updateLocation(latitude, longitude);
          setDebugLog(`Initial: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        }
      } catch (error) {
        setDebugLog('Native GPS error: ' + error.message);
        console.error('[RideContext] Native GPS error:', error);
      }
    }
  };

  const stopGPS = () => {
    if (locationSub.current) {
      locationSub.current.remove();
      locationSub.current = null;
    }
    if (locationInterval.current) {
      clearInterval(locationInterval.current);
      locationInterval.current = null;
    }
  };

  const cleanup = () => {
    stopPolling();
    stopGPS();
    stopAlert();
    setPendingRequests([]);
    console.log('[RideContext] cleanup → setActiveRide(null)');
    setActiveRide(null);
    setIsOnline(false);
    isFirstFetch.current = true;
    knownRideIds.current = new Set();
  };

  // ─── Driver actions ───
  const goOnline = useCallback(async () => {
    // Initialize Web Audio API during this user gesture (button press)
    // so that future startAlert() calls from useEffect will work
    initAudio();

    // Document check disabled for pilot — re-enable for production
    // try {
    //   const profileRes = await driverService.getProfile();
    //   if (profileRes.success) {
    //     const docs = profileRes.data.driverDocuments || [];
    //     const requiredTypes = ['DRIVERS_LICENSE', 'VEHICLE_INSURANCE', 'VEHICLE_INSPECTION'];
    //     const missing = requiredTypes.filter(type => {
    //       const doc = docs.find(d => d.type === type);
    //       return !doc || doc.status === 'REJECTED';
    //     });
    //     if (missing.length > 0) {
    //       const msg = 'Upload required documents before going online: ' +
    //         missing.map(t => t.replace(/_/g, ' ')).join(', ');
    //       if (isWeb) window.alert(msg);
    //       return;
    //     }
    //   }
    // } catch (err) {
    //   console.warn('[RideContext] Document check failed, allowing online:', err.message);
    // }

    setIsOnline(true);
    await AsyncStorage.setItem('driverOnlineStatus', JSON.stringify(true));
    isFirstFetch.current = true;
    console.log('[RideContext] goOnline: driver is now online, audio initialized');
    driverService.updateAvailability(true).catch(err =>
      console.warn('[RideContext] Backend availability update failed:', err.message)
    );
  }, []);

  const goOffline = useCallback(async () => {
    setIsOnline(false);
    await AsyncStorage.setItem('driverOnlineStatus', JSON.stringify(false));
    stopAlert();
    setPendingRequests([]);
    driverService.updateAvailability(false).catch(err =>
      console.warn('[RideContext] Backend availability update failed:', err.message)
    );
  }, []);

  const acceptRide = useCallback(async (rideId) => {
    stopAlert();
    try {
      const response = await driverService.acceptRide(rideId);
      if (response.success) {
        console.log('[RideContext] acceptRide → setActiveRide', response.data.ride.id, response.data.ride.status);
        setActiveRide(response.data.ride);
        setPendingRequests([]);
        return { success: true, ride: response.data.ride };
      }
      return { success: false, message: response.message || 'Failed to accept ride' };
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to accept ride';
      return { success: false, message: msg };
    }
  }, []);

  const declineRide = useCallback((rideId) => {
    setPendingRequests(prev => {
      const filtered = prev.filter(r => r.id !== rideId);
      if (filtered.length === 0) {
        stopAlert();
      }
      return filtered;
    });
  }, []);

  const arriveAtPickup = useCallback(async () => {
    if (!activeRide) return { success: false, message: 'No active ride' };
    try {
      const response = await driverService.arriveAtPickup(activeRide.id);
      if (response.success) {
        console.log('[RideContext] arriveAtPickup → status ARRIVED, ride', activeRide.id);
        setActiveRide(prev => ({ ...prev, status: 'ARRIVED' }));
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to mark arrival' };
    }
  }, [activeRide]);

  const startTrip = useCallback(async () => {
    if (!activeRide) return { success: false, message: 'No active ride' };
    try {
      const response = await driverService.startRide(activeRide.id);
      if (response.success) {
        console.log('[RideContext] startTrip → status IN_PROGRESS, ride', activeRide.id);
        setActiveRide(prev => ({ ...prev, status: 'IN_PROGRESS' }));
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to start ride' };
    }
  }, [activeRide]);

  const completeRide = useCallback(async () => {
    if (!activeRide) return { success: false, message: 'No active ride' };
    try {
      const response = await driverService.completeRide(activeRide.id);
      if (response.success) {
        const result = {
          success: true,
          ride: response.data.ride,
          earnings: response.data.earnings,
        };
        console.log('[RideContext] completeRide → setActiveRide(null), ride', activeRide.id);
        setActiveRide(null);
        // Resume polling for new rides
        isFirstFetch.current = true;
        return result;
      }
      return { success: false, message: response.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to complete ride' };
    }
  }, [activeRide]);

  const cancelRide = useCallback(async () => {
    if (!activeRide) return { success: false, message: 'No active ride' };
    try {
      const response = await driverService.cancelRide(activeRide.id);
      if (response.success) {
        console.log('[RideContext] cancelRide → setActiveRide(null), ride', activeRide.id);
        setActiveRide(null);
        isFirstFetch.current = true;
        return { success: true, message: response.message };
      }
      return { success: false, message: response.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to cancel ride' };
    }
  }, [activeRide]);

  const value = {
    // State
    isOnline,
    location,
    lastGpsUpdate,
    debugLog,
    pendingRequests,
    activeRide,
    driverMode,

    // Actions
    goOnline,
    goOffline,
    acceptRide,
    declineRide,
    arriveAtPickup,
    startTrip,
    completeRide,
    cancelRide,
  };

  return (
    <RideContext.Provider value={value}>
      {children}
    </RideContext.Provider>
  );
};
